import { useId, useRef, useCallback, type KeyboardEvent } from 'react';
import * as s from './SegmentedControl.css';

export interface Segment<Id extends string = string> {
  id: Id;
  label: string;
  badge?: number;
  /** 배지 숫자에 대한 SR 라벨. 미지정 시 `${badge}개`로 폴백. */
  badgeLabel?: string;
  /** 배지의 뜻. `alert`(기본) = 처리할 건수, amber 채움. `count` = 서랍의 크기, 배경 없는 조용한 숫자. */
  badgeTone?: 'alert' | 'count';
}

/**
 * 밀도 축. `md` 는 페이지 폭을 쓰는 탭 줄, `sm` 은 표 옆에 앉는 밀집 컨트롤
 * (글자 12px · 패딩 4×10 · 라운드 4 · 배지 11px).
 */
export type SegmentedControlSize = 'md' | 'sm';

/**
 * 화면·창 전환 **탭**이다 — `role="tablist"` 에 방향키가 곧 선택이라 다른 일에 쓰면 뜻이 틀린다.
 * 조건 필터·정렬은 `Button variant="chip"` + `aria-pressed`, 폼 값 선택은 `Radio` 가 자리다
 * (DESIGN.md §표면 축 4단 `chip`). 2026-09-17 까지 `tone="quiet"` 로 그 둘을 여기서 받았는데
 * 시각만 낮추고 역할은 못 바꿔서 걷었다.
 */
export interface SegmentedControlProps<Id extends string = string> {
  segments: readonly Segment<Id>[];
  activeId: Id;
  onSelect: (id: Id) => void;
  ariaLabel?: string;
  /** tab id prefix. 미지정 시 useId()로 생성. 연결된 tabpanel과 같은 baseId를 공유하려면 전달. */
  id?: string;
  /** 각 segment가 제어하는 tabpanel의 id 매핑 (aria-controls). */
  panelIdById?: Partial<Record<Id, string>>;
  /** 밀도. 미지정 시 `md` — 기존 소비처 무변경. */
  size?: SegmentedControlSize;
}

export function SegmentedControl<Id extends string = string>({
  segments,
  activeId,
  onSelect,
  ariaLabel,
  id,
  panelIdById,
  size = 'md',
}: SegmentedControlProps<Id>) {
  const groupRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const baseId = id ?? generatedId;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      let nextIndex: number | null = null;

      if (e.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % segments.length;
      } else if (e.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + segments.length) % segments.length;
      } else if (e.key === 'Home') {
        nextIndex = 0;
      } else if (e.key === 'End') {
        nextIndex = segments.length - 1;
      }

      const nextSegment = nextIndex !== null ? segments[nextIndex] : undefined;
      if (nextIndex !== null && nextSegment) {
        e.preventDefault();
        const buttons = groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
        buttons?.[nextIndex]?.focus();
        onSelect(nextSegment.id);
      }
    },
    [segments, onSelect]
  );

  return (
    <div ref={groupRef} className={s.group} role="tablist" aria-label={ariaLabel}>
      {segments.map((seg, index) => {
        const isActive = seg.id === activeId;
        return (
          <button
            key={seg.id}
            id={`${baseId}-seg-${seg.id}`}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={panelIdById?.[seg.id]}
            tabIndex={isActive ? 0 : -1}
            className={s.button({ active: isActive, size })}
            onClick={() => onSelect(seg.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            {seg.label}
            {seg.badge != null && seg.badge > 0 && (
              <span
                className={s.badge({ size, tone: seg.badgeTone ?? 'alert' })}
                aria-label={seg.badgeLabel ?? `${seg.badge}개`}
              >
                {seg.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
